import { BrandKitField, BrandKitFieldType } from './brand-kit-field';

export enum BrandKitFieldChangeStatus {
  Added = 'added',
  Removed = 'removed',
  Changed = 'changed',
}

export enum BrandKitFieldAttribute {
  Label = 'label',
  Description = 'description',
  /** Includes a list's item type. */
  Type = 'type',
  Value = 'value',
}

export interface BrandKitFieldDiff {
  key: string;
  status: BrandKitFieldChangeStatus;
  before?: BrandKitField;
  after?: BrandKitField;
  /** Which attributes differ; empty unless `status` is `changed`. */
  changed: BrandKitFieldAttribute[];
  /** Items added to/removed from a list whose value changed. */
  listItems?: { added: string[]; removed: string[] };
}

export interface BrandKitDiff {
  /** Changed fields, in `after` order followed by removed fields. */
  fields: BrandKitFieldDiff[];
  /** True when fields present in both were reordered. */
  reordered: boolean;
}

const valuesEqual = (a: BrandKitField, b: BrandKitField): boolean => {
  if (a.type === BrandKitFieldType.Font && b.type === BrandKitFieldType.Font) {
    // jsonb doesn't preserve object key order, so compare members.
    return (
      a.value?.family === b.value?.family &&
      a.value?.cssUrl === b.value?.cssUrl &&
      a.value?.fallback === b.value?.fallback &&
      a.value?.assetId === b.value?.assetId
    );
  }
  if (Array.isArray(a.value) && Array.isArray(b.value)) {
    const bv = b.value;
    return a.value.length === bv.length && a.value.every((v, i) => v === bv[i]);
  }
  return a.value === b.value;
};

const typesEqual = (a: BrandKitField, b: BrandKitField): boolean =>
  a.type === b.type &&
  (a.type !== BrandKitFieldType.List ||
    (b.type === BrandKitFieldType.List && a.itemType === b.itemType));

const diffField = (
  before: BrandKitField,
  after: BrandKitField,
): BrandKitFieldDiff | undefined => {
  const changed: BrandKitFieldAttribute[] = [];
  if (before.label !== after.label) {
    changed.push(BrandKitFieldAttribute.Label);
  }
  if (before.description !== after.description) {
    changed.push(BrandKitFieldAttribute.Description);
  }
  if (!typesEqual(before, after)) {
    changed.push(BrandKitFieldAttribute.Type);
  }
  if (!valuesEqual(before, after)) {
    changed.push(BrandKitFieldAttribute.Value);
  }
  if (changed.length === 0) {
    return undefined;
  }

  const diff: BrandKitFieldDiff = {
    key: after.key,
    status: BrandKitFieldChangeStatus.Changed,
    before,
    after,
    changed,
  };
  if (Array.isArray(before.value) && Array.isArray(after.value)) {
    const beforeItems = new Set(before.value);
    const afterItems = new Set(after.value);
    diff.listItems = {
      added: after.value.filter((v) => !beforeItems.has(v)),
      removed: before.value.filter((v) => !afterItems.has(v)),
    };
  }
  return diff;
};

/** What changed going from `before` to `after`, matching fields by key. */
export const diffBrandKitFields = (
  before: readonly BrandKitField[],
  after: readonly BrandKitField[],
): BrandKitDiff => {
  const beforeByKey = new Map(before.map((f) => [f.key, f]));
  const afterKeys = new Set(after.map((f) => f.key));
  const fields: BrandKitFieldDiff[] = [];

  for (const field of after) {
    const old = beforeByKey.get(field.key);
    if (!old) {
      fields.push({
        key: field.key,
        status: BrandKitFieldChangeStatus.Added,
        after: field,
        changed: [],
      });
      continue;
    }
    const diff = diffField(old, field);
    if (diff) {
      fields.push(diff);
    }
  }

  for (const field of before) {
    if (!afterKeys.has(field.key)) {
      fields.push({
        key: field.key,
        status: BrandKitFieldChangeStatus.Removed,
        before: field,
        changed: [],
      });
    }
  }

  const commonBefore = before.filter((f) => afterKeys.has(f.key));
  const commonAfter = after.filter((f) => beforeByKey.has(f.key));
  const reordered = commonBefore.some((f, i) => f.key !== commonAfter[i].key);

  return { fields, reordered };
};

export const isBrandKitDiffEmpty = (diff: BrandKitDiff): boolean =>
  diff.fields.length === 0 && !diff.reordered;
